import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import T from 'i18n';
import { Modal } from 'features/common/MaterialBasedComponents';
import {
  Alert,
  Box,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import api from 'common/api';
import { selectCurrentCaseId } from 'common/selectors';
import { compareTabValues } from '../tabUtils';

type LateInsertModalProps = {
  open: boolean;
  files: File[];
  assignments: Record<string, string>;
  defaultTab: string;
  existingFiles: Array<{ name: string; tab: string }>;
  onAssignmentChange: (fileName: string, tab: string) => void;
  onRemoveFile: (fileName: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  submitted: boolean;
  folderName?: string;
  folderId: string;
};

const processError = (error: any) => {
  switch (error?.status) {
    default:
      if (typeof error?.body === 'object')
        return error.body.body || error.body.statusText || error.body.message || '';
      else return error?.body || error?.statusText || '';
  }
};

const formatSize = (size?: number) => {
  if (!size || Number.isNaN(size)) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const LateInsertModal = ({
  open,
  files,
  assignments,
  defaultTab,
  existingFiles,
  onAssignmentChange,
  onRemoveFile,
  onClose,
  onSubmit,
  submitted,
  folderName,
  folderId,
}: LateInsertModalProps) => {
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [editingOriginalTab, setEditingOriginalTab] = useState<string>('');
  const [availableTabs, setAvailableTabs] = useState<string[]>([]);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [wholeNumbersOnly, setWholeNumbersOnly] = useState(false);
  const preEditScrollRef = useRef<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const previousTabsRef = useRef<string[]>([]);

  const filesToUpload = useSelector((state: any) => state.case.uploadFiles);
  const uploadFilesPending = useSelector((state: any) => state.case.uploadFilesPending);

  const caseId = useSelector(selectCurrentCaseId);

  const hasFailures = useMemo(
    () => (filesToUpload || []).some((f: any) => !!f?.error),
    [filesToUpload],
  );

  const isUploading = uploadFilesPending > 0;

  const showStatus =
    submitted || ((isUploading || hasFailures) && filesToUpload && filesToUpload.length > 0);

  const resetLocalState = useCallback(() => {
    setEditingRowKey(null);
    setEditingValue('');
    setEditingOriginalTab('');
    setAvailableTabs([]);
    setLoadingTabs(false);
    setWholeNumbersOnly(false);
    previousTabsRef.current = [];
    preEditScrollRef.current = null;
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, []);

  const handleSave = () => {
    if (!files.length || submitted) return;
    onSubmit();
  };

  const getAllTabs = useCallback(async (wholeNumbers: boolean) => {
    try {
      const url = `/cases/${caseId}/folders/${folderId}/late-inserts${
        wholeNumbers ? '?wholeNumbersOnly=true' : ''
      }`;
      const response: any = await api.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching late insert tabs:', error);
    }
  }, [caseId, folderId]);

  const normalizedExistingTabs = useMemo(() => {
    const set = new Set<string>();
    existingFiles.forEach((f) => {
      if (f.tab) set.add(f.tab.trim().toLowerCase());
    });
    return set;
  }, [existingFiles]);

  const assignmentErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    const used = new Set<string>();
    files.forEach((file) => {
      const val = (assignments[file.name] || '').trim();
      const normalized = val.toLowerCase();
      if (!val) {
        errors[file.name] = T.translate('case.lateInsertTabRequired');
        return;
      }
      if (!/^[0-9]+(\.[0-9]+)*$/.test(val)) {
        errors[file.name] = T.translate('case.lateInsertTabFormat');
        return;
      }
      if (normalizedExistingTabs.has(normalized)) {
        errors[file.name] = T.translate('case.lateInsertTabInUse');
        return;
      }
      if (used.has(normalized)) {
        errors[file.name] = T.translate('case.lateInsertTabDuplicate');
        return;
      }
      used.add(normalized);
    });
    return errors;
  }, [assignments, files, normalizedExistingTabs]);

  const disableSave = !files.length || submitted || Object.keys(assignmentErrors).length > 0;

  const combinedRows = useMemo(() => {
    const rows: Array<{
      key: string;
      name: string;
      tab: string;
      isNew: boolean;
      file?: File;
    }> = [];

    (existingFiles || []).forEach((row, idx) => {
      rows.push({
        key: `existing-${idx}`,
        name: row.name,
        tab: row.tab || '',
        isNew: false,
      });
    });

    files.forEach((file, idx) => {
      const backendTab = availableTabs?.[idx] || '';
      const currentAssignment = assignments[file.name];
      const tabValue =
        currentAssignment != null && currentAssignment !== '' ? currentAssignment : backendTab;
      rows.push({
        key: `new-${file.name}-${idx}`,
        name: file.name,
        tab: tabValue || '',
        isNew: true,
        file,
      });
    });
    return rows.sort((a, b) => {
      const aTab =
        editingRowKey && a.key === editingRowKey && editingOriginalTab ? editingOriginalTab : a.tab;
      const bTab =
        editingRowKey && b.key === editingRowKey && editingOriginalTab ? editingOriginalTab : b.tab;
      return compareTabValues(aTab, bTab);
    });
  }, [existingFiles, files, assignments, editingRowKey, editingOriginalTab, availableTabs]);

  useEffect(() => {
    if (editingRowKey === null && preEditScrollRef.current !== null && scrollContainerRef.current) {
      const top = preEditScrollRef.current;
      preEditScrollRef.current = null;
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = top;
        }
      });
    }
  }, [editingRowKey]);

  useEffect(() => {
    if (!open) {
      resetLocalState();
    }
  }, [open, resetLocalState]);

  useEffect(() => {
    if (!open) return;
    if (!availableTabs || availableTabs.length === 0) return;

    files.forEach((file, idx) => {
      const backendTab = availableTabs[idx];
      if (!backendTab) return;
      const current = assignments[file.name];
      const previousTab = previousTabsRef.current[idx];
      const shouldReplace =
        !current || current === '' || (previousTab && current === previousTab);
      // Seed from backend whenever the current value is empty or missing so
      // cleared fields are refilled with the suggested tab.
      if (shouldReplace && current !== backendTab) {
        onAssignmentChange(file.name, backendTab);
      }
    });
    previousTabsRef.current = availableTabs;
  }, [open, availableTabs, files, assignments, onAssignmentChange]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingTabs(true);
    (async () => {
      try {
        const tabs = await getAllTabs(wholeNumbersOnly);
        if (!cancelled && Array.isArray(tabs)) {
          const sortedTabs = [...tabs].sort(compareTabValues);
          setAvailableTabs(sortedTabs as any);
        }
      } finally {
        if (!cancelled) {
          setLoadingTabs(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, getAllTabs, wholeNumbersOnly]);

  useEffect(() => {
    if (!open) return;

    const timeoutId = window.setTimeout(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [open]);

  return (
    <Modal
      show={open}
      handleClose={onClose}
      handleSave={handleSave}
      disableSave={disableSave}
      saveTitle={T.translate('case.lateInsertStartUpload')}
      title={T.translate('case.lateInsert')}
      closeTitle={T.translate('generic.close')}
    >
      <Stack spacing={2}>
        <Alert color="info" severity="info">
          {T.translate('case.lateInsertDescription', { folderName: folderName || '' })}
          <br />
          {T.translate('case.lateInsertDefaultHelper', { tab: defaultTab })}
        </Alert>
        {files.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {T.translate('case.lateInsertTabHelp')}
          </Typography>
        )}
        {files.length > 0 && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <FormControlLabel
              control={
                <Checkbox
                  checked={wholeNumbersOnly}
                  onChange={(e) => setWholeNumbersOnly(e.target.checked)}
                  disabled={loadingTabs}
                />
              }
              label={T.translate('case.lateInsertWholeNumbersToggle')}
            />
          </Stack>
        )}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {T.translate('case.lateInsertExisting')}
          </Typography>
          {loadingTabs ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                minHeight: 160,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : combinedRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {T.translate('case.lateInsertNoExisting')}
            </Typography>
          ) : (
            <Box
              ref={scrollContainerRef}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                maxHeight: '60vh',
                overflow: 'auto',
              }}
            >
              <Box
                display="grid"
                gridTemplateColumns="3fr 1fr 80px"
                sx={{
                  px: 1,
                  py: 0.75,
                  minHeight: 44,
                  alignItems: 'center',
                  borderBottom: '1px solid #f0f0f0',
                  bgcolor: '#fafafa',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {T.translate('common.file')}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {T.translate('case.tab')}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>
                  {T.translate('common.actions')}
                </Typography>
              </Box>
              {combinedRows.map((row, idx) => {
                const isNew = row.isNew;
                const errorText = isNew ? assignmentErrors[row.name] : '';
                const tabValue = editingRowKey === row.key ? editingValue : row.tab;
                return (
                  <Box
                    key={row.key}
                    display="grid"
                    gridTemplateColumns="3fr 1fr 80px"
                    alignItems="center"
                    sx={{
                      px: 1,
                      py: 0.75,
                      minHeight: 48,
                      borderBottom: '1px solid #f1f1f1ff',
                      bgcolor: isNew ? '#ffffffff' : '#f1f1f1ff',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                      {isNew && <InsertDriveFileIcon fontSize="small" color="action" />}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap title={row.name}>
                          {row.name}
                        </Typography>
                        {isNew && row.file && (
                          <Typography variant="caption" color="text.secondary">
                            {formatSize(row.file.size)} {row.file.type ? `• ${row.file.type}` : ''}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {isNew ? (
                      <TextField
                        size="small"
                        value={tabValue}
                        onFocus={() => {
                          setEditingRowKey(row.key);
                          setEditingValue(tabValue);
                          setEditingOriginalTab(row.tab);
                          preEditScrollRef.current = scrollContainerRef.current
                            ? scrollContainerRef.current.scrollTop
                            : null;
                        }}
                        onBlur={() => {
                          onAssignmentChange(row.name, editingValue);
                          setEditingRowKey(null);
                          setEditingOriginalTab('');
                          setEditingValue('');
                        }}
                        onChange={(e) => {
                          const next = e.target.value;
                          // Allow only digits and dots in the input. Empty string is also allowed.
                          if (/^[0-9.]*$/.test(next)) {
                            setEditingValue(next);
                          }
                        }}
                        onKeyDown={(e) => {
                          const allowedControlKeys = [
                            'Backspace',
                            'Delete',
                            'ArrowLeft',
                            'ArrowRight',
                            'ArrowUp',
                            'ArrowDown',
                            'Tab',
                            'Home',
                            'End',
                            'Enter',
                          ];
                          if (
                            allowedControlKeys.includes(e.key) ||
                            // Allow any key with meta/ctrl for shortcuts (copy/paste etc.)
                            e.metaKey ||
                            e.ctrlKey ||
                            // Allow digits and dot
                            /[0-9.]/.test(e.key)
                          ) {
                            return;
                          }
                          e.preventDefault();
                        }}
                        sx={{
                          alignSelf: 'center',
                          '& .MuiInputBase-root': { height: 38, alignItems: 'center' },
                          '& .MuiInputBase-input': { py: 0.5 },
                          '& .MuiFormHelperText-root': { m: 0, lineHeight: 1 },
                        }}
                        InputProps={{
                          sx: {
                            backgroundColor: '#ffffffff',
                            borderColor: '#f8f7f7ff',
                            alignItems: 'center',
                          },
                        }}
                        error={Boolean(errorText)}
                        helperText={errorText || undefined}
                        disabled={submitted}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {row.tab}
                      </Typography>
                    )}
                    {isNew && !submitted ? (
                      <IconButton size="small" onClick={() => onRemoveFile(row.name)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <span />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
        {showStatus && (
          <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1.5 }}>
            <Typography variant="subtitle2" color="text.primary">
              {T.translate('case.lateInsertUploadSummary')}
            </Typography>
            <Stack spacing={1.25} sx={{ mt: 1 }}>
              {filesToUpload && filesToUpload.length > 0 ? (
                filesToUpload.map((f: any, idx: number) => {
                  const progress = parseFloat(f?.progress || '0');
                  const pct = Math.min(Math.max(progress, 0), 100);
                  const isError = !!f?.error;
                  const errMsg = isError ? processError(f.error) : '';
                  const isDuplicate =
                    isError &&
                    (/this file is a duplicate of/i.test(errMsg) || f?.error?.status === 409);
                  const isOversized =
                    isError && (f?.error?.status === 413 || /file.*exceeds.*limit/i.test(errMsg));
                  const label = isError ? errMsg : `${pct.toFixed(0)}%`;
                  return (
                    <Box key={`${f.hash || f.name}-${idx}`}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="body2"
                          sx={{
                            pr: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {f.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={
                            isError
                              ? isDuplicate || isOversized
                                ? 'warning.main'
                                : 'error.main'
                              : 'text.secondary'
                          }
                        >
                          {label}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={isError ? 100 : pct}
                        color={isError && !isDuplicate && !isOversized ? 'error' : 'primary'}
                        sx={{
                          height: 6,
                          borderRadius: 6,
                          mt: 0.5,
                          ...((isDuplicate || isOversized) && {
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: (theme) => (theme as any).palette.warning.main,
                            },
                            backgroundColor: (theme) => (theme as any).palette.warning.light,
                          }),
                        }}
                      />
                    </Box>
                  );
                })
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {T.translate('generic.noUploads') || 'No uploads yet'}
                </Typography>
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Modal>
  );
};

export default LateInsertModal;
